import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { useHistory } from 'react-router';
import { getIDFromUrl } from '@rossum/api-client';
import { ResizablePane } from '@rossum/components/resizable-pane/ResizablePane';
import { EmptyStateRadar } from '@rossum/ui';
import { Box, Stack } from '@rossum/ui/material';
import { constructDocumentUrl } from '../../../lib/url';
import { refetchAnnotation } from '../../../redux/modules/annotation/actions';
import { State } from '../../../types/state';
import { notify } from '../../toaster';
import {
  useFetchNeighbors,
  useNeighborAnnotationsMetadata,
  useSetNeighborTrainingEnabled,
} from '../core';
import { NeighborCard } from './NeighborCard';
import { NeighborsTopBar } from './NeighborsTopBar';

const REVIEWED_PANE_STORAGE_KEY = 'document-neighbors:reviewed-pane-width';
/** Keeps the left pane usable and always leaves room for two pages on the right. */
const REVIEWED_PANE_MIN_WIDTH = 240;
const NEIGHBORS_PANE_MIN_WIDTH = 480;

/** `spacing(4)` of padding on each pane, `spacing(2)` between the columns. */
const PANE_PADDING = 32;
const GRID_COLUMN_GAP = 16;

/**
 * The layout reads as three equal columns: the reviewed page, then two
 * neighbors. Solve for all three pages coming out the same width, where W is
 * the window, P the pane padding and G the grid gap:
 *
 *   L - 2P = (W - L - 2P - G) / 2   =>   L = (W + 2P - G) / 3
 */
const getEqualThirdsWidth = () =>
  Math.max(
    REVIEWED_PANE_MIN_WIDTH,
    Math.round((window.innerWidth + PANE_PADDING * 2 - GRID_COLUMN_GAP) / 3)
  );

const ScrollPane = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      height: 1,
      overflowY: 'auto',
      // 24px to the top bar, 32px on the other three sides.
      pt: 3,
      px: 4,
      pb: 4,
    }}
  >
    {children}
  </Box>
);

type NeighborsPageProps = {
  annotationId: number;
  pageNumber: number;
  focusedSchemaId: string | null;
};

export const NeighborsPage = ({
  annotationId,
  pageNumber,
  focusedSchemaId,
}: NeighborsPageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useFetchNeighbors(annotationId);
  const loadedAnnotation = useSelector((state: State) => state.annotation);

  const { width: reviewedPaneWidth, ref: reviewedPaneRef } =
    useResizeDetector<HTMLDivElement>({ handleHeight: false });
  const { width: neighborsPaneWidth, ref: neighborsPaneRef } =
    useResizeDetector<HTMLDivElement>({ handleHeight: false });

  // Two neighbors side by side only while there is more room for them than for
  // the page they are compared against. A comparison of the panes, not a
  // breakpoint: the drag handle moves them independently of the viewport.
  const neighborColumns =
    reviewedPaneWidth && neighborsPaneWidth
      ? reviewedPaneWidth > neighborsPaneWidth
        ? 1
        : 2
      : 2;

  const { annotationIds, neighbors } = useMemo(() => {
    const currentPage = data?.pages.find(p => p.pageNumber === pageNumber);
    const neighbors = currentPage?.neighbors ?? [];
    const neighborAnnotationIds = neighbors.flatMap(neighbor =>
      neighbor.annotationUrl ? [getIDFromUrl(neighbor.annotationUrl)] : []
    );
    const annotationIds = [annotationId, ...neighborAnnotationIds];
    return { neighbors, annotationIds };
  }, [pageNumber, data?.pages, annotationId]);

  const { metadataByAnnotationId } =
    useNeighborAnnotationsMetadata(annotationIds);

  const { mutate: setTrainingEnabled } = useSetNeighborTrainingEnabled({
    onSuccess: ({ annotationId: toggledId }) => {
      // Keep the validation screen's sidebar toggle in sync for the way back.
      if (loadedAnnotation.id === toggledId && loadedAnnotation.url) {
        dispatch(refetchAnnotation(loadedAnnotation.url));
      }
    },
    onError: () =>
      notify.error({
        title: intl.formatMessage({
          id: 'components.sidebarV2.annotationData.trainingToggle.error',
        }),
      }),
  });

  const handleToggleTraining = (toggledId: number, enabled: boolean) =>
    setTrainingEnabled({ annotationId: toggledId, enabled });

  return (
    <Stack
      sx={{
        height: 1,
        width: 1,
        overflow: 'hidden',
        backgroundColor: 'background.default',
      }}
    >
      <NeighborsTopBar
        pageNumber={pageNumber}
        onBack={() => history.push(constructDocumentUrl({ id: annotationId }))}
      />
      <Stack direction="row" sx={{ flex: 1, minHeight: 0, width: 1 }}>
        <ResizablePane
          storageKey={REVIEWED_PANE_STORAGE_KEY}
          paneConfig={{
            min: REVIEWED_PANE_MIN_WIDTH,
            max: window.innerWidth - NEIGHBORS_PANE_MIN_WIDTH,
            initial: getEqualThirdsWidth(),
          }}
        >
          <Stack
            ref={reviewedPaneRef}
            direction="row"
            sx={{
              width: 1,
              height: 1,
              borderRight: t => `1px solid ${t.palette.divider}`,
            }}
          >
            <ScrollPane>
              <NeighborCard
                variant="reviewed"
                isLoading={isLoading}
                annotationId={annotationId}
                pageNumber={pageNumber}
                focusedSchemaId={focusedSchemaId}
                metadata={metadataByAnnotationId.get(annotationId)}
                onToggleTraining={handleToggleTraining}
              />
            </ScrollPane>
          </Stack>
        </ResizablePane>

        <Stack
          ref={neighborsPaneRef}
          direction="row"
          sx={{ flex: 1, minWidth: 0, height: 1 }}
        >
          <ScrollPane>
            {isError ? (
              <EmptyStateRadar
                title={intl.formatMessage({
                  id: 'components.neighborsDrawer.error.title',
                })}
                description={intl.formatMessage({
                  id: 'components.neighborsDrawer.error.description',
                })}
              />
            ) : !isLoading && neighbors.length === 0 ? (
              <EmptyStateRadar
                title={intl.formatMessage({
                  id: 'components.neighborsDrawer.noResults.title',
                })}
                description={intl.formatMessage({
                  id: 'components.neighborsDrawer.noResults.description',
                })}
              />
            ) : (
              // The neighbors in a grid, wrapping — never scrolling sideways.
              <Box
                sx={{
                  display: 'grid',
                  columnGap: `${GRID_COLUMN_GAP}px`,
                  // Clears the labels of the card above before the next page.
                  rowGap: 3,
                  gridTemplateColumns: `repeat(${neighborColumns}, minmax(0, 1fr))`,
                }}
              >
                {neighbors.map(neighbor => {
                  const neighborAnnotationId = neighbor.annotationUrl
                    ? getIDFromUrl(neighbor.annotationUrl)
                    : null;

                  return (
                    <NeighborCard
                      key={`${neighborAnnotationId ?? neighbor.annotationUrl ?? 'unknown'}-${neighbor.pageNumber}`}
                      variant="neighbor"
                      isLoading={isLoading}
                      annotationId={neighborAnnotationId}
                      pageNumber={neighbor.pageNumber}
                      similarity={neighbor.similarity}
                      focusedSchemaId={focusedSchemaId}
                      metadata={
                        neighborAnnotationId
                          ? metadataByAnnotationId.get(neighborAnnotationId)
                          : undefined
                      }
                      link={
                        neighborAnnotationId
                          ? `/document/${neighborAnnotationId}?page=${neighbor.pageNumber}`
                          : undefined
                      }
                      onToggleTraining={handleToggleTraining}
                    />
                  );
                })}
              </Box>
            )}
          </ScrollPane>
        </Stack>
      </Stack>
    </Stack>
  );
};
